/**
 * Credit Pack Generator
 * Generate bank-formatted credit pack PDFs
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreditPackGenerator = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [generating, setGenerating] = useState(false);
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/business-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const generateCreditPack = async () => {
    if (!selectedPlan) return;
    
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/credit-packs/generate`,
        {
          plan_id: selectedPlan,
          include_scorecard: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Credit pack generated successfully!');
      fetchCreditPacks();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const fetchCreditPacks = async () => {
    if (!selectedPlan) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/credit-packs/plan/${selectedPlan}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPacks(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Credit Pack Generator</h1>
        <p className="text-gray-600 mt-1">Generate bank-formatted credit assessment packs</p>
      </div>

      {/* Generator */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">📝 Generate New Credit Pack</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Select Business Plan</label>
          <select
            value={selectedPlan}
            onChange={(e) => {
              setSelectedPlan(e.target.value);
              if (e.target.value) fetchCreditPacks();
            }}
            className="w-full border rounded-lg px-4 py-2"
          >
            <option value="">Choose a plan...</option>
            {plans.map(plan => (
              <option key={plan.id} value={plan.id}>
                {plan.name} - {plan.company_name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Sections to Include</label>
          <div className="space-y-2">
            {[
              'Cover Page',
              'Executive Summary',
              'Company Information',
              'Market Analysis',
              'Financial Projections (3 years)',
              'Financial Ratios (DSCR, Liquidity, Leverage)',
              'Risk Assessment',
              'Bank Evaluation & Scorecard',
              'Recommendation'
            ].map((section, idx) => (
              <label key={idx} className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="rounded" />
                <span className="text-sm">{section}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={generateCreditPack}
          disabled={!selectedPlan || generating}
          className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 font-medium"
        >
          {generating ? '🔄 Generating...' : '📝 Generate Credit Pack'}
        </button>
      </div>

      {/* Generated Packs */}
      {packs.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">📄 Generated Credit Packs</h2>
          <div className="space-y-3">
            {packs.map(pack => (
              <div key={pack.id} className="border rounded-lg p-4 flex justify-between items-center">
                <div>
                  <div className="font-medium">Version {pack.pack_version}</div>
                  <div className="text-sm text-gray-600">
                    Generated: {new Date(pack.generated_at).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {pack.version_stamp}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                    📥 Download PDF
                  </button>
                  <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                    👁️ Preview
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditPackGenerator;